// OKLab perceptual colour space (Björn Ottosson, 2020).
//
// Why: lerping rainbow gradients in sRGB produces muddy mid-tones. Lerping in
// OKLab produces vibrant, perceptually-uniform transitions — the visual
// difference is night and day for any multi-hue gradient.
//
// Round trip is sRGB (8-bit) → linear → OKLab → linear → sRGB (8-bit).

import type { RGB } from './color';

// ---- sRGB ⇄ linear ---------------------------------------------------------

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(1, v)) * 255);
}

// ---- linear sRGB ⇄ OKLab ---------------------------------------------------

export type OkLab = readonly [number, number, number];

export function srgbToOklab(rgb: RGB): OkLab {
  const r = srgbToLinear(rgb[0]);
  const g = srgbToLinear(rgb[1]);
  const b = srgbToLinear(rgb[2]);

  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return [
    0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  ];
}

export function oklabToSrgb(lab: OkLab): RGB {
  const L = lab[0], a = lab[1], b_ = lab[2];

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b_;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b_;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b_;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const r =  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b =  -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  return [linearToSrgb(r), linearToSrgb(g), linearToSrgb(b)];
}

// ---- Lerp ------------------------------------------------------------------

export function lerpOklab(a: OkLab, b: OkLab, t: number): OkLab {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/** Lerp two sRGB colors via OKLab. Returns sRGB. */
export function lerpRGBOklab(a: RGB, b: RGB, t: number): RGB {
  return oklabToSrgb(lerpOklab(srgbToOklab(a), srgbToOklab(b), t));
}
